//Updated ProcedureSave.tsx

import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomTextField } from "../../components/CustomFields/TextField";
import { LoadingButton } from "@mui/lab";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import {
  CustomSelect,
  SimpleAsyncSelect,
} from "../../components/CustomFields/CustomSelect";
//import { Procedure } from "../../shared/Model/OPD/Procedure";
import ReactQuill from "react-quill";
import { PreferencesContext } from "../../PreferenceContext";
import {
  CreateProcedure,
  GetProcedureDetailByID,
  GetProcedureList,
  UpdateProcedure,
} from "../../services/ProcedureService";
import { GetItemDetail, GetItemList } from "../../services/ItemService";
import {
  GetMultipleTeamMemberList,
  GetTeamMemberDetail,
} from "../../services/TeamMemberService";
import { AlertType } from "../../services/models/AlertType";
import { PAGE_SIZE } from "../../services/common/const";
import { useQuery } from "../../shared/CommonFunction";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import parse from "html-react-parser";
import ItemSave from "../Item/ItemSave";
import TeamMemberSave from "../Team/Staff/TeamMemberSave";
import { GetDepartmentList } from "../../services/DepartmentService";
import { GetSpecialityList } from "../../services/SpecialityService";

const GapUnitOptions = [
  { label: "Day", value: "DAY" },
  { label: "Week", value: "WEEK" },
  { label: "Month", value: "MONTH" },
];

const inputPropsForDecimalNumberField: any = {
  className: "no-spinner",
  inputMode: "decimal",
  pattern: "[0-9]*",
  step: "any",
  min: 0,
  align: "right",
  onKeyPress: (event: any) => {
    if (!/[0-9.]/.test(event.key)) {
      event.preventDefault();
    }
  },
};

const inputPropsForNumberField: any = {
  className: "no-spinner",
  inputMode: "decimal",
  pattern: "[0-9]*",
  step: "any",
  min: 0,
  style: { textAlign: "right" as const },

  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
    // block everything except digits + control keys
    if (
      !/[0-9]/.test(event.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
        event.key,
      )
    ) {
      event.preventDefault();
    }
  },
};

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  //investigationId: string;
  departmentId: string;
  branchId: string;
  orgId: string;
  consumerId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}
// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  orgId: "",
  consumerId: "",
};

interface formInitialValueTypes {
  procedureId: number;
  procedureName: string;
  description: string;
  price: number;
  durationHoursValue: number;
  durationMinuteValue: number;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  procedureVisitColl: Array<IProcedureVisit>;
}
interface IProcedureVisit {
  procedureVisitId?: number;
  procedureId: number;
  procedureName: string;
  price: number;
  durationHoursValue: number;
  durationMinuteValue: number;
  description?: string;
  prevProcedureVisitId?: number;
  gapFromPrevVisitValue: string;
  gapFromPrevVisitCode: string;
  gapFromPrevVisitName: string;
  isDeleted: boolean;
  displayOrder: number;
  mode: string;
  procedureVisitConsumableColl: Array<IProcedureVisitConsumable>;
  procedureVisitTeamMemberColl: Array<IProcedureVisitTeamMember>;
  procedureVisitMedicineColl: Array<IProcedureVisitMedicineData>;
}
interface IProcedureVisitTeamMember {
  procedureVisitTeamMemberId?: number | null;
  teamMemberId: number;
  teamMemberName: string;
  teamMemberRole: string;
  qualificationName: string;
  displayOrder: number;
  parentDisplayOrder: number;
  isAnesthetist: boolean;
  isDeleted: boolean;
  mode: string;
}

interface IProcedureVisitConsumable {
  procedureVisitConsumableId?: number;
  itemId: number;
  itemName: string;
  quantity: number;
  unitPrice: number;
  isDeleted: boolean;
  displayOrder: number;
  parentDisplayOrder: number;
  unitName: string;
  mode: string;
}

// ✅ NEW: Medicine data structure
interface IProcedureVisitMedicineData {
  procedureVisitMedicineId?: number;
  isMedicineNoteEnabled: boolean;
  medicineNote: string;
  isInstructionNoteEnabled: boolean;
  instructionNote: string;
  parentDisplayOrder: number;
  isDeleted: boolean;
  procedureVisitMedicineDetailColl: Array<IMedicineDetail>;
  procedureVisitMedicineInstructionColl: Array<IMedicineInstruction>;
  mode: string;
  displayOrder: number;
}

interface IMedicineDetail {
  procedureVisitMedicineDetailId?: number;
  medicineId: number;
  medicineName: string;
  medicineDoseId: number;
  displayOrder: number;
  printUnit?: string;
  routeOfAdministrationId?: number;
  quantityUnit?: string;
  relationToFoodTimeGapId?: number;
  isDeleted: boolean;
  mode: string;
}

interface IMedicineInstruction {
  procedureVisitMedicineInstructionId?: number;
  instructionId: number;
  instructionData: string;
  displayOrder: number;
  isDeleted: boolean;
  mode: string;
}
interface IPopupVisitCollDialog {
  data: Array<any>;
  open: boolean;
  totalCount: number;
  visitIndex?: number;
}

const MultipleProcedureVisit = (Props: any) => {
  //Props
  const {
    isForPopUp = false,
    handleClosePopUp,
    isFieldEnabled,
    isMemberProcedure = false,
    memberProcedureData = null,
    procedureNames = null,
  } = Props;
  const edService = new EncryptDecryptService();
  const [param, setParam] = useState(parameters);

  const navigate = useNavigate();

  // let urlParams = useQuery();
  // let curRawProcedureId = urlParams.get("q");
  // let urlProcedureId = 0;

  // if (curRawProcedureId !== null && curRawProcedureId !== undefined) {
  //   let decId = edService.decrypt(curRawProcedureId);
  //   if (decId !== "" && decId !== null && decId !== undefined) {
  //     urlProcedureId = +decId;
  //   }
  // } else {
  //   navigate("/not-found");
  // }
  let urlParams = useQuery();
  let curRawProcedureId = urlParams.get("q");
  let urlProcedureId = 0;

  // Popup/MemberProcedure mode mein URL param nahi hota — redirect skip karo
  if (!isForPopUp && !isMemberProcedure) {
    if (curRawProcedureId !== null && curRawProcedureId !== undefined) {
      let decId = edService.decrypt(curRawProcedureId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        urlProcedureId = +decId;
      }
    } else {
      navigate("/not-found");
    }
  }

  // API Integration - Add these state variables
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { userContext, updateUserContext } = useContext(PreferencesContext);

  const [consumableColl, setConsumableColl] = useState<any[]>([]);
  const [teamMembersColl, setTeamMembersColl] = useState<any[]>([]);
  const [visitProceduresColl, setVisitProceduresColl] = useState<any[]>([]);
  // Popover State (FIXED)
  const [openVisit, setOpenVisit] = useState(false);
  const [consumableMenuAnchor, setConsumableMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [teamMenuAnchor, setTeamMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [selectedConsumable, setSelectedConsumable] = useState<any>(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [specialityData, setSpecialityData] = useState<any[]>([]);
  const [departmentData, setDepartmentData] = useState<any[]>([]);

  const [customPopUp, setCustomPopUp] = React.useState<{
    open: boolean;
    name: string;
    code: string;
    data: any;
  }>({
    open: false,
    name: "",
    code: "",
    data: null,
  });

  const [openConsumableCollDilog, setOpenConsumableCollDilog] =
    React.useState<IPopupVisitCollDialog>({
      data: [],
      open: false,
      totalCount: 0,
    });

  const [openTeamMemberCollDilog, setOpenTeamMemberCollDilog] =
    React.useState<IPopupVisitCollDialog>({
      data: [],
      open: false,
      totalCount: 0,
    });
  const [openMedicineCollDilog, setOpenMedicineCollDilog] =
    React.useState<IPopupVisitCollDialog>({
      data: [],
      open: false,
      totalCount: 0,
    });

  const [openInstructionCollDilog, setOpenInstructionCollDilog] =
    React.useState<IPopupVisitCollDialog>({
      data: [],
      open: false,
      totalCount: 0,
    });

  const handleCloseDilog = (value: any) => {
    setOpenConsumableCollDilog({ ...openConsumableCollDilog, open: value });
    setOpenTeamMemberCollDilog({ ...openTeamMemberCollDilog, open: value });
    setOpenMedicineCollDilog({ ...openMedicineCollDilog, open: value });
    setOpenInstructionCollDilog({ ...openInstructionCollDilog, open: value });
  };

  const handleClickOpenPopup = (
    value: boolean,
    label: string,
    code: string,
    data: any,
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
      data: data,
    });
  };

  const handleClosePopup = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  const fetchItemList = async (searchValue: string, oninit: boolean) => {
    try {
      let params = new URLSearchParams();
      let items: any[] = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("productTypeCodes", "HSN,SAC");
      const result: any = await GetItemList(params);
      if (result?.data?.data?.itemColl) {
        items = result?.data?.data?.itemColl;
        if (oninit) setConsumableColl(items);
      }
      return items;
    } catch (error) {
      return [];
    }
  };

  const loadConsumableList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      fetchItemList(inputValue?.trim(), false).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  const fetchTeamMemberList = async (searchValue: string, oninit: boolean) => {
    try {
      let params = new URLSearchParams();
      let members: any[] = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("teamMemberType", "");

      const result: any = await GetMultipleTeamMemberList(params);
      if (result?.data?.data?.teamMemberColl) {
        members = result?.data?.data?.teamMemberColl;
        if (oninit) setTeamMembersColl(members);
      }
      return members;
    } catch (error) {
      return [];
    }
  };

  const loadTeamMembersList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      fetchTeamMemberList(inputValue?.trim(), false).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  const getItemDetail = async (itemId: number) => {
    let params = new URLSearchParams();
    params.append("itemId", itemId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    try {
      const res: any = await GetItemDetail(params);
      return res?.data?.data;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  const getTeamMemberDetailById = async (teamMemberId: number) => {
    let params = new URLSearchParams();
    params.append("teamMemberId", teamMemberId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetTeamMemberDetail(params);
      return res?.data?.data;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: e?.message,
        alertType: AlertType.Error,
      });
    }
  };

  const refreshConsumableDialog = (visitIndex: number) => {
    if (!openConsumableCollDilog.open) return;
    const path = `procedureVisitColl.${visitIndex}.procedureVisitConsumableColl`;
    const updated = (methods.getValues as any)(path) || [];
    const active = (updated as any[]).filter((c: any) => !c.isDeleted);
    setOpenConsumableCollDilog((prev) => ({
      ...prev,
      data: active,
      totalCount: active.length,
    }));
  };

  const refreshTeamMemberDialog = (visitIndex: number) => {
    if (!openTeamMemberCollDilog.open) return;
    const path = `procedureVisitColl.${visitIndex}.procedureVisitTeamMemberColl`;
    const updated = (methods.getValues as any)(path) || [];
    const active = (updated as any[]).filter((t: any) => !t.isDeleted);
    setOpenTeamMemberCollDilog((prev) => ({
      ...prev,
      data: active,
      totalCount: active.length,
    }));
  };

  const getConsumableTotal = (items: any[] = []) => {
    return items
      .filter((c: any) => !c?.isDeleted)
      .reduce(
        (acc: number, c: any) =>
          acc + Number(c?.quantity || 0) * Number(c?.unitPrice || 0),
        0,
      );
  };

  const recomputeVisitPrice = (
    visitIndex: number,
    overrideConsumables?: any[],
  ) => {
    const visitPath = `procedureVisitColl.${visitIndex}`;
    const visit = (methods.getValues as any)(visitPath);
    if (!visit) return 0;

    const consumables =
      overrideConsumables ?? visit?.procedureVisitConsumableColl ?? [];
    const consumableTotal = getConsumableTotal(consumables);
    const nextPrice = consumableTotal;

    (methods.setValue as any)(`${visitPath}.price`, nextPrice, {
      shouldDirty: true,
      shouldValidate: true,
    });

    return nextPrice;
  };

  const recomputeTotalPrice = (overrideVisits?: any[]) => {
    const visits =
      overrideVisits ?? (methods.getValues as any)("procedureVisitColl") ?? [];

    const total = visits
      .filter((v: any) => !v?.isDeleted)
      .reduce((acc: number, v: any) => acc + Number(v?.price || 0), 0);

    methods.setValue("price", total, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleAddConsumableToVisit = (
    selectedItem: any,
    visitIndex: number,
  ) => {
    const path = `procedureVisitColl.${visitIndex}.procedureVisitConsumableColl`;
    const detailColl = (methods.getValues as any)(path) || [];
    const maxRowNumber = detailColl.reduce(
      (acc: number, curr: any) =>
        curr.displayOrder > acc ? curr.displayOrder : acc,
      -1,
    );

    const newRow: IProcedureVisitConsumable = {
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      procedureVisitConsumableId: 0,
      itemId: selectedItem?.itemId,
      itemName: selectedItem?.itemName,
      quantity: 1,
      unitPrice: selectedItem?.unitAmount || 0,
      unitName: selectedItem?.unitName || "-",
      mode: "I",
      isDeleted: false,
      parentDisplayOrder:
        (methods.getValues as any)(
          `procedureVisitColl.${visitIndex}.displayOrder`,
        ) ?? visitIndex,
    };

    (methods.setValue as any)(path, [...detailColl, newRow], {
      shouldValidate: true,
      shouldDirty: true,
    });

    const nextConsumables = [...detailColl, newRow];
    recomputeVisitPrice(visitIndex, nextConsumables);
    recomputeTotalPrice();
  };

  const handleAddTeamMember = (selected: any, visitIndex: number) => {
    const path = `procedureVisitColl.${visitIndex}.procedureVisitTeamMemberColl`;
    const detailColl = (methods.getValues as any)(path) || [];
    const maxRowNumber = detailColl.reduce(
      (acc: number, curr: any) =>
        curr.displayOrder > acc ? curr.displayOrder : acc,
      -1,
    );

    const newRow: IProcedureVisitTeamMember = {
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      procedureVisitTeamMemberId: 0,
      teamMemberId: selected?.teamMemberId || selected?.value,
      teamMemberName: selected?.fullName || selected?.label || "",
      teamMemberRole: selected?.teamMemberCode || selected?.role || "",
      qualificationName: selected?.qualification || "",
      parentDisplayOrder:
        (methods.getValues as any)(
          `procedureVisitColl.${visitIndex}.displayOrder`,
        ) ?? visitIndex,
      isAnesthetist: selected?.isAnesthetist || false,
      isDeleted: false,
      mode: "I",
    };

    (methods.setValue as any)(path, [...detailColl, newRow], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleEditConsumable = () => {
    if (!selectedConsumable) return;
    const { visitIndex } = selectedConsumable;
    const path = `procedureVisitColl.${visitIndex}.procedureVisitConsumableColl`;
    const rows = (methods.getValues as any)(path) || [];
    const arrayIndex = rows.findIndex(
      (r: any) => r.displayOrder === selectedConsumable.displayOrder,
    );
    const row = rows[arrayIndex];
    if (row) {
      setSelectedConsumable((prev: any) => ({ ...prev, arrayIndex }));
      handleClickOpenPopup(true, "", "UPDATE_ITEM", row);
    }
    setConsumableMenuAnchor(null);
  };

  const handleEditTeamMember = () => {
    if (!selectedTeamMember) return;
    const { visitIndex } = selectedTeamMember;
    const path = `procedureVisitColl.${visitIndex}.procedureVisitTeamMemberColl`;
    const rows = (methods.getValues as any)(path) || [];
    const arrayIndex = rows.findIndex(
      (r: any) => r.displayOrder === selectedTeamMember.displayOrder,
    );
    const row = rows[arrayIndex];
    if (row) {
      setSelectedTeamMember((prev: any) => ({ ...prev, arrayIndex }));
      handleClickOpenPopup(true, "", "UPDATE_TEAMMEMBER", row);
    }
    setTeamMenuAnchor(null);
  };

  const handleDeleteConsumableRow = (visitIndex: number, row: any) => {
    const path = `procedureVisitColl.${visitIndex}.procedureVisitConsumableColl`;
    const consumableColl = (methods.getValues as any)(path) || [];

    const updatedRows = consumableColl.map((item: any) =>
      item.displayOrder === row.displayOrder
        ? { ...item, isDeleted: true, mode: "D" }
        : item,
    );

    (methods.setValue as any)(path, updatedRows, {
      shouldDirty: true,
      shouldValidate: true,
    });

    recomputeVisitPrice(visitIndex, updatedRows);
    recomputeTotalPrice();
  };

  const handleDeleteTeamMemberRow = (visitIndex: number, row: any) => {
    const path = `procedureVisitColl.${visitIndex}.procedureVisitTeamMemberColl`;
    const rows = (methods.getValues as any)(path) || [];

    const updatedRows = rows.map((item: any) =>
      item.displayOrder === row.displayOrder
        ? { ...item, isDeleted: true, mode: "D" }
        : item,
    );

    (methods.setValue as any)(path, updatedRows, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleConsumableFieldChange = (
    visitIndex: number,
    displayOrder: number,
    field: "quantity" | "unitPrice",
    rawValue: string,
  ) => {
    const path = `procedureVisitColl.${visitIndex}.procedureVisitConsumableColl`;
    const rows: IProcedureVisitConsumable[] =
      (methods.getValues as any)(path) || [];

    const index = rows.findIndex((r) => r.displayOrder === displayOrder);
    if (index === -1) return;

    const updatedRows = rows.map((r, i) =>
      i === index ? { ...r, [field]: Number(rawValue) || 0 } : r,
    );

    (methods.setValue as any)(path, updatedRows, {
      shouldDirty: true,
      shouldValidate: true,
    });

    recomputeVisitPrice(visitIndex, updatedRows);
    recomputeTotalPrice();
    refreshConsumableDialog(visitIndex);
  };

  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await handleSave(data);
    setIsLoading(false);
  };
  const handleSaveAndTemplate = async () => {
    setIsLoading(true);
    await methods.handleSubmit(async (data: any) => {
      await handleSave(data, true);
    }, onError)();
    setIsLoading(false);
  };
  const onError = (errors: any) => {
    console.log("Form errors:", errors);
  };

  const getVisitDisplayName = (name?: string | null) => {
    if (!name) return "";
    const normalized = name.trim();
    const numberedMatch = normalized.match(/#\d+\s*-\s*(.+)$/);
    if (numberedMatch?.[1]) return numberedMatch[1].trim();

    const segments = normalized
      .split(" - ")
      .map((s) => s.trim())
      .filter(Boolean);

    if (segments.length > 1) {
      return segments[segments.length - 1];
    }

    return normalized;
  };

  const getNormalizedDurationText = () => {
    const hrs = Number(methods.watch("durationHoursValue") || 0);
    const mins = Number(methods.watch("durationMinuteValue") || 0);

    const totalMinutes = hrs * 60 + mins;

    const normalizedHrs = Math.floor(totalMinutes / 60);
    const normalizedMins = totalMinutes % 60;

    if (normalizedHrs === 0 && normalizedMins === 0) {
      return "";
    }

    if (normalizedHrs === 0) {
      return `${normalizedMins} mins`;
    }

    if (normalizedMins === 0) {
      return `${normalizedHrs} hrs`;
    }

    return `${normalizedHrs} hrs ${normalizedMins} mins`;
  };
  // Form initialization
  const initProcedureData: formInitialValueTypes = {
    procedureId: 0,
    procedureName: isForPopUp ? procedureNames : "",
    description: "",
    price: 0,
    durationHoursValue: 0,
    durationMinuteValue: 0,
    departmentId:
      userContext?.departmentId > 0
        ? {
            label: userContext?.departmentName || "",
            value: userContext?.departmentId?.toString(),
          }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
            label: userContext?.specialityName || "",
            value: userContext?.specialityId?.toString(),
          }
        : null,
    // procedureVisitConsumableColl: [],
    // procedureVisitTeamMemberColl: [],
    // procedureVisitMedicineColl: [],
    procedureVisitColl: [],
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    procedureId: Yup.number().nullable(),
    procedureName: Yup.string().required(),
    description: Yup.string().nullable(),
    price: Yup.number().required(),
    durationHoursValue: Yup.number().nullable(),
    durationMinuteValue: Yup.number().nullable(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    procedureVisitColl: Yup.array()
      .of(
        Yup.object().shape({
          procedureVisitId: Yup.number().nullable(),
          procedureId: Yup.number().nullable(),
          procedureName: Yup.string().nullable(),
          durationHoursValue: Yup.number().nullable(),
          durationMinuteValue: Yup.number().nullable(),
          prevProcedureVisitId: Yup.number().nullable(),
          gapFromPrevVisitValue: Yup.string().nullable(),
          gapFromPrevVisitCode: Yup.string().nullable(),
          gapFromPrevVisitName: Yup.string().nullable(),
          displayOrder: Yup.number().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),

          procedureVisitConsumableColl: Yup.array()
            .of(
              Yup.object().shape({
                procedureVisitConsumableId: Yup.number().nullable(),
                itemId: Yup.number().required(),
                itemName: Yup.string().nullable(),
                quantity: Yup.number().required(),
                unitPrice: Yup.number().required(),
                unitName: Yup.string().nullable(),
                isDeleted: Yup.boolean().required(),
                displayOrder: Yup.number().required(),
                parentDisplayOrder: Yup.number().required(),
                mode: Yup.string().required(),
              }),
            )
            .required("Visit is required."),
          procedureVisitTeamMemberColl: Yup.array()
            .of(
              Yup.object().shape({
                procedureVisitTeamMemberId: Yup.number().nullable(),
                teamMemberId: Yup.number().required(),
                teamMemberName: Yup.string().nullable(),
                teamMemberRole: Yup.string().required(),
                qualificationName: Yup.string().nullable(),
                displayOrder: Yup.number().required(),
                parentDisplayOrder: Yup.number().required(),
                isDeleted: Yup.boolean().required(),
                isAnesthetist: Yup.boolean().nullable(),
                mode: Yup.string().nullable(),
              }),
            )
            .required("Visit is required."),
          procedureVisitMedicineColl: Yup.array()
            .of(
              Yup.object().shape({
                procedureVisitMedicineId: Yup.number().nullable(),
                isMedicineNoteEnabled: Yup.boolean().nullable(),
                medicineNote: Yup.string().nullable(),
                isInstructionNoteEnabled: Yup.boolean().nullable(),
                instructionNote: Yup.string().nullable(),
                mode: Yup.string().nullable(),
                parentDisplayOrder: Yup.number().required(),
                displayOrder: Yup.number().required(),
                procedureVisitMedicineDetailColl: Yup.array().of(
                  Yup.object().shape({
                    procedureVisitMedicineDetailId: Yup.number().nullable(),
                    medicineId: Yup.number().nullable(),
                    medicineDoseId: Yup.number().nullable(),
                    displayOrder: Yup.number().nullable(),
                    printUnit: Yup.string().nullable(),
                    routeOfAdministrationId: Yup.number().nullable(),
                    quantityUnit: Yup.string().nullable(),
                    relationToFoodTimeGapId: Yup.number().nullable(),
                    isDeleted: Yup.boolean().nullable(),
                    mode: Yup.string().nullable(),
                  }),
                ),

                procedureVisitMedicineInstructionColl: Yup.array().of(
                  Yup.object().shape({
                    procedureVisitMedicineInstructionId:
                      Yup.number().nullable(),
                    instructionId: Yup.number().nullable(),
                    instructionData: Yup.string().nullable(),
                    displayOrder: Yup.number().nullable(),
                    isDeleted: Yup.boolean().nullable(),
                    mode: Yup.string().nullable(),
                  }),
                ),
              }),
            )
            .required("Visit is required."),
        }),
      )
      .required("Visit is required."),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initProcedureData,
  });

  const procedureVisit = methods.watch("procedureVisitColl") || [];

  const handleSingleProcedureVisitAdd = async (value: any) => {
    const detailColl = methods.getValues("procedureVisitColl") || [];
    const details = methods.getValues();
    const activeRows = detailColl.filter((r: any) => !r.isDeleted);
    const maxRowNumber = activeRows.reduce(
      (acc: number, curr: any) =>
        curr.displayOrder > acc ? curr.displayOrder : acc,
      0,
    );

    let consumables: any[] = [];
    let teamMembers: any[] = [];
    let medicineColl: IProcedureVisitMedicineData[] = [];
    const parentDisplayOrder = activeRows.length === 0 ? 0 : maxRowNumber + 1;
    const prevVisit =
      activeRows.length > 0 ? activeRows[activeRows.length - 1] : null;

    const result = await getSingleProcedureDetailById(value.procedureId);
    if (result) {
      const procedure = result.procedureVisitColl?.[0];

      consumables =
        procedure?.procedureVisitConsumableColl?.map(
          (item: any, index: number) => ({
            // procedureVisitConsumableId: item.procedureVisitConsumableId || 0,
            itemId: item.itemId,
            itemName: item.itemName || "",
            quantity: item.quantityUsed || 1,
            unitPrice: item.unitPrice || 0,
            unitName: item.unitName || "-",
            displayOrder: index,
            parentDisplayOrder,
            isDeleted: false,
            mode: "I",
          }),
        ) || [];

      teamMembers =
        procedure?.procedureVisitTeamMemberColl?.map(
          (member: any, index: number) => ({
            // procedureVisitTeamMemberId: member.procedureVisitTeamMemberId || 0,
            teamMemberId: member.teamMemberId,
            teamMemberName: member.fullName || "",
            teamMemberRole: member.teamMemberCode || "",
            qualificationName: member.qualification || "",
            displayOrder: index,
            parentDisplayOrder,
            isAnesthetist: member.isAnesthetist || false,
            isDeleted: false,
            mode: "I",
          }),
        ) || [];

      if (procedure?.procedureVisitMedicine) {
        const m = procedure.procedureVisitMedicine;

        medicineColl = [
          {
            isMedicineNoteEnabled: m.isMedicineNoteEnabled ?? false,
            medicineNote: m.medicineNote ?? "",
            isInstructionNoteEnabled: m.isInstructionNoteEnabled ?? false,
            instructionNote: m.instructionNote ?? "",
            displayOrder: parentDisplayOrder,
            parentDisplayOrder,
            isDeleted: false,
            mode: "I",

            procedureVisitMedicineDetailColl: (
              m.procedureVisitMedicineDetailColl || []
            ).map((d: any) => ({
              ...d,
              procedureVisitMedicineDetailId: 0,
              isDeleted: false,
              mode: "I",
            })),

            procedureVisitMedicineInstructionColl: (
              m.procedureVisitMedicineInstructionColl || []
            ).map((i: any) => ({
              ...i,
              procedureVisitMedicineInstructionId: 0,
              isDeleted: false,
              mode: "I",
            })),
          },
        ];
      }
    }

    const sumDurationInHours =
      (details.durationHoursValue || 0) + (value.durationHoursValue || 0);
    const sumDurationInMinute =
      (details.durationMinuteValue || 0) + (value.durationMinuteValue || 0);
    const consumableTotal = getConsumableTotal(consumables);
    const visitPrice = consumableTotal;
    const newRow: IProcedureVisit = {
      displayOrder: activeRows.length === 0 ? 0 : maxRowNumber + 1,
      procedureId: value.procedureId,
      procedureName: value.name,
      durationHoursValue: value.durationHoursValue,
      durationMinuteValue: value.durationMinuteValue,
      description: value.description,
      prevProcedureVisitId: prevVisit?.procedureVisitId || 0,
      gapFromPrevVisitValue: "",
      gapFromPrevVisitCode: "DAY",
      gapFromPrevVisitName: "Day",
      isDeleted: false,
      mode: "I",
      price: visitPrice,
      procedureVisitConsumableColl: consumables,
      procedureVisitTeamMemberColl: teamMembers,
      procedureVisitMedicineColl: medicineColl,
    };
    methods.setValue("procedureVisitColl", [...detailColl, newRow], {
      shouldValidate: true,
    });

    recomputeTotalPrice([...detailColl, newRow]);
    methods.setValue("durationHoursValue", sumDurationInHours);
    methods.setValue("durationMinuteValue", sumDurationInMinute);
  };

  const handleDeleteProcedure = (value: any) => {
    const rows = methods.getValues("procedureVisitColl") || [];
    const details = methods.getValues();

    const updatedRows = rows.map((row: any) =>
      row.displayOrder === value.displayOrder
        ? {
            ...row,
            isDeleted: true,
            mode: "D",
            procedureVisitConsumableColl:
              row.procedureVisitConsumableColl?.map((c: any) => ({
                ...c,
                isDeleted: true,
                mode: "D",
              })) || [],
            procedureVisitTeamMemberColl:
              row.procedureVisitTeamMemberColl?.map((t: any) => ({
                ...t,
                isDeleted: true,
                mode: "D",
              })) || [],
            procedureVisitMedicineColl:
              row.procedureVisitMedicineColl?.map((m: any) => ({
                ...m,
                isDeleted: true,
                mode: "D",
                procedureVisitMedicineDetailColl:
                  m.procedureVisitMedicineDetailColl?.map((d: any) => ({
                    ...d,
                    isDeleted: true,
                    mode: "D",
                  })) || [],
                procedureVisitMedicineInstructionColl:
                  m.procedureVisitMedicineInstructionColl?.map((i: any) => ({
                    ...i,
                    isDeleted: true,
                    mode: "D",
                  })) || [],
              })) || [],
          }
        : row,
    );
    const sumDurationInHours =
      (details.durationHoursValue || 0) - (value.durationHoursValue || 0);
    const sumDurationInMinute =
      (details.durationMinuteValue || 0) - (value.durationMinuteValue || 0);

    methods.setValue("procedureVisitColl", updatedRows, {
      shouldDirty: true,
      shouldValidate: true,
    });
    recomputeTotalPrice(updatedRows);
    methods.setValue("durationHoursValue", sumDurationInHours);
    methods.setValue("durationMinuteValue", sumDurationInMinute);
  };

  const handleManagePopup = async (code: string, data?: any) => {
    const capturedConsumable = selectedConsumable;
    const capturedTeamMember = selectedTeamMember;

    setCustomPopUp({ ...customPopUp, open: false });

    if (code === "ITEM_CREATE") {
      const item = await getItemDetail(data?.itemId);
      if (!item || !capturedConsumable) return;
      handleAddConsumableToVisit(item, capturedConsumable.visitIndex);
      refreshConsumableDialog(capturedConsumable.visitIndex);
      recomputeTotalPrice();
    } else if (code === "ITEM_UPDATE") {
      const item = await getItemDetail(data?.itemId);
      if (!item || !capturedConsumable) return;

      const { visitIndex, displayOrder, arrayIndex } = capturedConsumable;
      const collPath = `procedureVisitColl.${visitIndex}.procedureVisitConsumableColl`;
      const rows = (methods.getValues as any)(collPath) || [];

      const actualIndex =
        arrayIndex !== undefined
          ? arrayIndex
          : rows.findIndex((r: any) => r.displayOrder === displayOrder);

      if (actualIndex === -1) return;

      const rowPath = `${collPath}.${actualIndex}`;
      const existingRow = rows[actualIndex];

      (methods.setValue as any)(`${rowPath}.itemId`, item.itemId, {
        shouldDirty: true,
      });
      (methods.setValue as any)(`${rowPath}.itemName`, item.itemName, {
        shouldDirty: true,
      });
      (methods.setValue as any)(`${rowPath}.unitPrice`, item.unitAmount || 0, {
        shouldDirty: true,
        shouldValidate: true,
      });
      (methods.setValue as any)(`${rowPath}.unitName`, item.unitName || "-", {
        shouldDirty: true,
      });
      if ((existingRow?.procedureVisitConsumableId || 0) > 0) {
        (methods.setValue as any)(`${rowPath}.mode`, "U", {
          shouldDirty: true,
        });
      }
      refreshConsumableDialog(visitIndex);
      const nextRows = (methods.getValues as any)(collPath) || [];
      recomputeVisitPrice(visitIndex, nextRows);
      recomputeTotalPrice();
    } else if (code === "TEAM_MEMBER_CREATE") {
      const value = await getTeamMemberDetailById(data?.teamMemberId);
      if (!value || !capturedTeamMember) return;
      handleAddTeamMember(value, capturedTeamMember.visitIndex);
      refreshTeamMemberDialog(capturedTeamMember.visitIndex);
    } else if (code === "TEAM_MEMBER_UPDATE") {
      const value = await getTeamMemberDetailById(data?.teamMemberId);
      if (!value || !capturedTeamMember) return;

      const { visitIndex, displayOrder, arrayIndex } = capturedTeamMember;
      const collPath = `procedureVisitColl.${visitIndex}.procedureVisitTeamMemberColl`;
      const rows = (methods.getValues as any)(collPath) || [];

      const actualIndex =
        arrayIndex !== undefined
          ? arrayIndex
          : rows.findIndex((r: any) => r.displayOrder === displayOrder);

      if (actualIndex === -1) return;

      const rowPath = `${collPath}.${actualIndex}`;
      const existingRow = rows[actualIndex];

      (methods.setValue as any)(
        `${rowPath}.teamMemberId`,
        value.value || value.teamMemberId,
        { shouldDirty: true },
      );
      (methods.setValue as any)(
        `${rowPath}.teamMemberName`,
        value.label || value.teamMemberName || "",
        { shouldDirty: true },
      );
      (methods.setValue as any)(
        `${rowPath}.teamMemberRole`,
        value.role || value.teamMemberRole || "",
        { shouldDirty: true },
      );
      (methods.setValue as any)(
        `${rowPath}.qualificationName`,
        value.qualification || value.qualificationName || "",
        { shouldDirty: true, shouldValidate: true },
      );
      if ((existingRow?.procedureVisitTeamMemberId || 0) > 0) {
        (methods.setValue as any)(`${rowPath}.mode`, "U", {
          shouldDirty: true,
        });
      }
      refreshTeamMemberDialog(visitIndex);
    }
  };

  const handleSave = async (
    value: formInitialValueTypes,
    shouldSaveTemplate = false,
  ) => {
    setIsSaving(true);
    // const visit = value?.procedureVisitColl?.[0];
    const visits = value?.procedureVisitColl || [];
    // const medicine = visit?.procedureVisitMedicineColl || [];
    // const consumableColl = visit?.procedureVisitConsumableColl || [];
    // const teamMemberColl = visit?.procedureVisitTeamMemberColl || [];

    const procedureVisitcoll = visits.map((x: any) => ({
      procedureVisitId: x.procedureVisitId || 0,
      procedureId: x.procedureId || 0,
      procedureName: x.procedureName || "",
      description: x.description || "",
      price: x.price || 0,
      prevProcedureVisitId: x.prevProcedureVisitId || 0,
      durationHoursValue: x.durationHoursValue || 0,
      durationMinuteValue: x.durationMinuteValue || 0,
      gapFromPrevVisitValue: x.gapFromPrevVisitValue || "",
      gapFromPrevVisitCode: x.gapFromPrevVisitCode || "",
      gapFromPrevVisitName: x.gapFromPrevVisitName || "",
      displayOrder: x.displayOrder || 0,
      isDeleted: x.isDeleted || false,
      mode: x.mode || "I",
    }));

    const consumableColl = visits.reduce(
      (acc: any[], curr: any) =>
        acc.concat(curr?.procedureVisitConsumableColl || []),
      [],
    );
    const teamMemberColl = visits.reduce(
      (acc: any[], curr: any) =>
        acc.concat(curr?.procedureVisitTeamMemberColl || []),
      [],
    );
    const medicineColl = visits.reduce(
      (acc: any[], visit: any, visitIndex: number) => {
        (visit?.procedureVisitMedicineColl || []).forEach(
          (m: any, mIndex: number) => {
            const uniqueMedicineDisplayOrder = visitIndex;
            acc.push({
              procedureVisitMedicineId: m.procedureVisitMedicineId || 0,
              isMedicineNoteEnabled: m.isMedicineNoteEnabled || false,
              medicineNote: m.medicineNote || null,
              isInstructionNoteEnabled: m.isInstructionNoteEnabled || false,
              instructionNote: m.instructionNote || null,
              parentDisplayOrder: visit.displayOrder || 0,
              displayOrder: uniqueMedicineDisplayOrder,
              isDeleted: m.isDeleted || false,
              mode: m.procedureVisitMedicineId > 0 ? "U" : "I",
            });
          },
        );
        return acc;
      },
      [],
    );

    const medicineDetailColl = visits.reduce(
      (acc: any[], visit: any, visitIndex: number) => {
        (visit?.procedureVisitMedicineColl || []).forEach(
          (m: any, mIndex: number) => {
            const uniqueMedicineDisplayOrder = visitIndex;
            (m?.procedureVisitMedicineDetailColl || []).forEach((d: any) => {
              acc.push({
                ...d,
                parentDisplayOrder: uniqueMedicineDisplayOrder,
              });
            });
          },
        );
        return acc;
      },
      [],
    );

    const medicineInstructionColl = visits.reduce(
      (acc: any[], visit: any, visitIndex: number) => {
        (visit?.procedureVisitMedicineColl || []).forEach(
          (m: any, mIndex: number) => {
            const uniqueMedicineDisplayOrder = visitIndex;
            (m?.procedureVisitMedicineInstructionColl || []).forEach(
              (i: any) => {
                acc.push({
                  ...i,
                  parentDisplayOrder: uniqueMedicineDisplayOrder,
                });
              },
            );
          },
        );
        return acc;
      },
      [],
    );

    try {
      let formData = {
        procedureId: value?.procedureId || 0,
        consumerId: userContext.consumerId,
        organizationId: userContext.organizationId,
        branchId: userContext.branchId,
        specialityId: value.specialityId?.value || 0,
        departmentId: value.departmentId?.value || 0,
        typeCode: "MULTIPLE_VISIT",
        name: value.procedureName,
        description: value?.description,
        price: Number(value.price) || 0,
        displayInOPD: true,
        durationHoursValue: Number(value.durationHoursValue) || 0,
        durationMinuteValue: value.durationMinuteValue,

        procedureVisitJson: JSON.stringify(procedureVisitcoll),
        procedureVisitConsumableJson: JSON.stringify(consumableColl),
        procedureVisitTeamMemberJson: JSON.stringify(teamMemberColl),
        procedureVisitMedicineJson:
          medicineColl.length > 0 ? JSON.stringify(medicineColl) : null,

        procedureVisitMedicineDetailJson:
          medicineDetailColl.length > 0
            ? JSON.stringify(medicineDetailColl)
            : null,

        procedureVisitMedicineInstructionJson:
          medicineInstructionColl.length > 0
            ? JSON.stringify(medicineInstructionColl)
            : null,

        isDeleted: false,
        dataManagedBy: userContext.loginDetail.userId,
        mode: value?.procedureId > 0 ? "U" : "I",
      };

      if (isMemberProcedure) {
        if (shouldSaveTemplate) {
          const templateResult: any = await CreateProcedure(formData);
          if (!templateResult?.data) {
            updateUserContext({
              ...userContext,
              isAlert: true,
              alertMessage: templateResult?.message || "Template save failed.",
              alertType: AlertType.Error,
            });
            return;
          }
        }

        // Multiple visit ke liye: har visit ka consumable/team/medicine nested rakho
        const returnData = {
          procedureName: value.procedureName,
          description: value.description,
          price: Number(value.price) || 0,
          durationHoursValue: Number(value.durationHoursValue) || 0,
          durationMinuteValue: Number(value.durationMinuteValue) || 0,
          procedureVisitTypeCode: "MULTIPLE_VISIT",
          parentDisplayOrder:
            memberProcedureData?.parentDisplayOrder ?? undefined,
          displayInOPD: true,
          // Multiple visits: har visit ka data fully nested
          procedureVisitColl: visits.map((x: any) => ({
            procedureVisitId: x.procedureVisitId || 0,
            procedureName: x.procedureName || "",
            description: x.description || "",
            price: x.price || 0,
            prevProcedureVisitId: x.prevProcedureVisitId || 0,
            durationHoursValue: x.durationHoursValue || 0,
            durationMinuteValue: x.durationMinuteValue || 0,
            gapFromPrevVisitValue: x.gapFromPrevVisitValue || "",
            gapFromPrevVisitCode: x.gapFromPrevVisitCode || "",
            gapFromPrevVisitName: x.gapFromPrevVisitName || "",
            displayOrder: x.displayOrder || 0,
            isDeleted: x.isDeleted || false,
            mode: x.mode || "I",
            // nested collections per-visit
            procedureVisitConsumableColl: (
              x.procedureVisitConsumableColl || []
            ).filter((c: any) => !c.isDeleted),
            procedureVisitTeamMemberColl: (
              x.procedureVisitTeamMemberColl || []
            ).filter((t: any) => !t.isDeleted),
            procedureVisitMedicineColl: (
              x.procedureVisitMedicineColl || []
            ).filter((m: any) => !m.isDeleted),
          })),
        };
        if (handleClosePopUp) {
          handleClosePopUp("VISIT_CREATE", returnData);
        }
        if (shouldSaveTemplate) {
          updateUserContext({
            ...userContext,
            isAlert: true,
            alertMessage: "Procedure added and template saved successfully.",
            alertType: AlertType.Success,
          });
        }
        setIsSaving(false);
        return;
      }

      let result: any = "";
      if (urlProcedureId > 0) {
        result = await UpdateProcedure(formData);
      } else {
        result = await CreateProcedure(formData);
      }
      if (result?.data) {
        navigate(-1);
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getSingleProcedureDetailById = async (procedureId: number) => {
    try {
      let procedureData: any;
      let params = new URLSearchParams();
      params.append("procedureId", procedureId?.toString());
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());

      const result: any = await GetProcedureDetailByID(params);
      if (result?.data?.data) {
        procedureData = result.data.data;
      }
      return procedureData;
    } catch (error) {
      console.error("Error fetching single visit procedures:", error);
      return [];
    }
  };

  const getProcedureDetailById = async (procedureId: number) => {
    try {
      let procedureData: any;
      let params = new URLSearchParams();
      params.append("procedureId", procedureId?.toString());
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("departmentId", userContext.departmentId?.toString());
      params.append("specialityId", userContext.specialityId?.toString());
      params.append("branchId", userContext.branchId?.toString());

      const result: any = await GetProcedureDetailByID(params);
      if (result?.data?.data) {
        procedureData = result.data.data;
        const visits = procedureData.procedureVisitColl || [];

        let detailColl = procedureData?.procedureVisitColl?.map(
          (x: any, xIndex: number) => {
            const displayOrder = x.displayOrder ?? xIndex;
            let procedureVisitDetail: IProcedureVisit = {
              procedureVisitId: x.procedureVisitId || 0,
              displayOrder,
              procedureId: x.procedureId,
              procedureName: x.procedureName,
              durationHoursValue: x.durationHoursValue,
              durationMinuteValue: x.durationMinuteValue,
              prevProcedureVisitId: x.prevProcedureVisitId || 0,
              gapFromPrevVisitValue: x.gapFromPrevVisitValue || "",
              gapFromPrevVisitCode: x.gapFromPrevVisitCode || "DAY",
              gapFromPrevVisitName: x.gapFromPrevVisitName || "Day",
              price: 0,
              isDeleted: false,
              mode: "U",
              procedureVisitConsumableColl:
                x.procedureVisitConsumableColl
                  ?.filter(
                    (mf: any) => x.procedureVisitId === mf.procedureVisitId,
                  )
                  ?.map((i: any, IIndex: number) => {
                    let Consumable: IProcedureVisitConsumable = {
                      parentDisplayOrder: xIndex,
                      displayOrder: IIndex,
                      procedureVisitConsumableId:
                        i.procedureVisitConsumableId || 0,
                      itemId: i.itemId,
                      itemName: i.itemName || "",
                      quantity: i.quantityUsed || 1,
                      unitPrice: i.unitPrice || 0,
                      unitName: i.unitName || "-",
                      isDeleted: false,
                      mode: "U",
                    };
                    return Consumable;
                  }) || [],
              procedureVisitTeamMemberColl: x.procedureVisitTeamMemberColl
                ?.filter(
                  (mf: any) => x.procedureVisitId === mf.procedureVisitId,
                )
                ?.map((i: any, IIndex: number) => {
                  let TeamMember: IProcedureVisitTeamMember = {
                    parentDisplayOrder: xIndex,
                    displayOrder: IIndex,
                    procedureVisitTeamMemberId:
                      i.procedureVisitTeamMemberId || 0,
                    teamMemberId: i.teamMemberId,
                    teamMemberName: i.fullName || "",
                    teamMemberRole: i.teamMemberCode || "",
                    isAnesthetist: i.isAnesthetist || false,
                    isDeleted: false,
                    mode: "U",
                    qualificationName: i.qualification || "",
                  };
                  return TeamMember;
                }),
              procedureVisitMedicineColl: x.procedureVisitMedicine
                ? [
                    {
                      parentDisplayOrder: xIndex,
                      displayOrder: 0,
                      procedureVisitMedicineId:
                        x.procedureVisitMedicine.procedureVisitMedicineId || 0,
                      isMedicineNoteEnabled:
                        x.procedureVisitMedicine.isMedicineNoteEnabled || false,
                      medicineNote: x.procedureVisitMedicine.medicineNote || "",
                      isInstructionNoteEnabled:
                        x.procedureVisitMedicine.isInstructionNoteEnabled ||
                        false,
                      instructionNote:
                        x.procedureVisitMedicine.instructionNote || "",
                      isDeleted: false,
                      mode: "U",

                      procedureVisitMedicineDetailColl:
                        x.procedureVisitMedicine.procedureVisitMedicineDetailColl?.map(
                          (i: any, IIndex: number) => ({
                            displayOrder: IIndex,
                            procedureVisitMedicineDetailId:
                              i.procedureVisitMedicineDetailId || 0,
                            medicineId: i.medicineId,
                            medicineName:
                              i.medicineName || i.medicineLabel || "",
                            medicineDoseId: i.medicineDoseId,
                            printUnit: i.printUnit || i.printUnitName || "",
                            routeOfAdministrationId:
                              i.routeOfAdministrationId || null,
                            quantityUnit: i.quantityUnit || "",
                            relationToFoodTimeGapId:
                              i.relationToFoodTimeGapId || null,
                            isDeleted: false,
                            mode:
                              i.procedureVisitMedicineDetailId > 0 ? "U" : "I",
                          }),
                        ),

                      procedureVisitMedicineInstructionColl:
                        x.procedureVisitMedicine.procedureVisitMedicineInstructionColl?.map(
                          (i: any, IIndex: number) => ({
                            displayOrder: IIndex,
                            procedureVisitMedicineInstructionId:
                              i.procedureVisitMedicineInstructionId || 0,
                            instructionId: i.instructionId,
                            instructionData: i.instructionData || "",
                            isDeleted: false,
                            mode:
                              i.procedureVisitMedicineInstructionId > 0
                                ? "U"
                                : "I",
                          }),
                        ),
                    },
                  ]
                : [],
            };
            const consumableTotal = getConsumableTotal(
              procedureVisitDetail.procedureVisitConsumableColl || [],
            );
            procedureVisitDetail.price = consumableTotal;
            return procedureVisitDetail;
          },
        );

        const totalPrice = (detailColl || [])
          .filter((v: any) => !v?.isDeleted)
          .reduce((acc: number, v: any) => acc + Number(v?.price || 0), 0);

        methods.reset(
          {
            ...initProcedureData,
            procedureId: procedureData.procedureId || 0,
            procedureName: procedureData.name || "",
            description: procedureData.description || "",
            price: totalPrice,
            departmentId: procedureData?.departmentId
              ? {
                  label: procedureData?.departmentName,
                  value: procedureData?.departmentId,
                }
              : null,
            specialityId: procedureData?.specialityId
              ? {
                  label: procedureData?.specialityName,
                  value: procedureData?.specialityId,
                }
              : null,
            durationHoursValue: procedureData.durationHoursValue || 0,
            durationMinuteValue: procedureData.durationMinuteValue || 0,
            procedureVisitColl: detailColl,
          },
          {
            keepErrors: true,
            keepIsValid: true,
            keepDirty: false,
          },
        );
        setOpenVisit(false);
      }
      return procedureData;
    } catch (error) {
      console.error("Error fetching single visit procedures:", error);
      return [];
    }
  };

  const fetchSingleVisitProcedures = async (
    searchValue: string,
    oninit: boolean,
  ) => {
    try {
      let params = new URLSearchParams();
      let procedureColl = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append(
        "departmentId",
        methods.getValues("departmentId")?.value ||
          userContext.departmentId?.toString(),
      );
      params.append(
        "specialityId",
        methods.getValues("specialityId")?.value ||
          userContext.specialityId?.toString(),
      );
      params.append("procedureTypeCode", "SUB_VISIT");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "");
      const result: any = await GetProcedureList(params);
      if (result?.data?.data?.procedureColl) {
        procedureColl = result?.data?.data?.procedureColl;
        if (oninit) setVisitProceduresColl(procedureColl);
      }
      return procedureColl;
    } catch (error) {
      console.error("Error fetching single visit procedures:", error);
      return [];
    }
  };

  // FILTER  DURATION UNIT
  async function filterProcedures(inputValue: string) {
    let result = await fetchSingleVisitProcedures(inputValue, false);
    return result;
  }
  //  DURATION UNIT LOAD ON SEARCH
  const loadProceduresList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterProcedures(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  //DEPARTMENT LIST
  const getDepartmentList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("ConsumerId", userContext?.consumerId.toString());
      params.append("OrgId", userContext?.organizationId.toString());
      params.append("BranchId", userContext?.branchId.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetDepartmentList(params);
      if (result?.data?.data?.departmentColl) {
        const resultData = result?.data?.data.departmentColl;
        setDepartmentData(resultData);
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  //Speciality LIST
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setSpecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const fetchData = async () => {
    try {
      await fetchSingleVisitProcedures("", true);
      if (urlProcedureId <= 0) {
        await getDepartmentList();
      }
      if (urlProcedureId > 0) {
        await getProcedureDetailById(urlProcedureId);
      } else if (userContext?.departmentId > 0) {
        await fetchSpeciality(userContext?.departmentId?.toString());
        if (userContext?.specialityId > 0) {
          await getProcedureDetailById(urlProcedureId);
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };
  // USEEFFECTS
  useEffect(() => {
    if (!userContext?.consumerId) return;
    fetchItemList("", true);
    fetchTeamMemberList("", true);
  }, [
    userContext?.consumerId,
    userContext?.branchId,
    userContext?.organizationId,
  ]);

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Box
        className={"main-container"}
        sx={{
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, onError)} action="">
            <Grid container spacing={0}>
              <Grid item xs={12} sx={{ px: 0, pb: 0 }}>
                <Grid
                  container
                  item
                  spacing={1.5}
                  sx={{ position: "relative" }}
                >
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.departmentId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={urlProcedureId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setSpecialityData([]);
                                  }
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.departmentId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.specialityId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              openMenuOnFocus
                              isClearable
                              isDisabled={urlProcedureId > 0}
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                methods.setValue(
                                  "specialityId",
                                  e?.value ? e : null,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                                if (e && e?.value) {
                                  setIsLoading(true);
                                  await fetchSingleVisitProcedures("", true);
                                  setIsLoading(false);
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.specialityId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel className="fileds-label-text">
                        Procedure Name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          name="procedureName"
                          control={methods.control}
                          render={({ field }) => (
                            <CustomTextField
                              {...field}
                              fullWidth
                              size="small"
                            />
                          )}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.procedureName?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} />

                  {isFieldEnabled("DESCRIPTION") && (
                    <Grid item xs={12} sx={{ mb: 0, pb: 0 }}>
                      <FormControl fullWidth sx={{ mb: 0, pb: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                          className="note-container-box"
                        >
                          <FormLabel
                            className="fileds-label-text"
                            sx={{ mb: 0.5 }}
                          >
                            Description
                          </FormLabel>
                          {/* <Typography sx={{ fontSize: 12, color: "#9e9e9e" }}>
                            {description.length} / {maxChars}
                          </Typography> */}
                        </Box>
                        <Controller
                          control={methods.control}
                          name="description"
                          defaultValue=""
                          render={({ field }) => (
                            <ReactQuill
                              theme="snow"
                              value={field.value || ""}
                              onChange={field.onChange}
                              className="react-quill-alignment"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        mt: 2,
                        position: "relative",
                      }}
                    >
                      {procedureVisit
                        .filter((item) => !item.isDeleted)
                        .map((item, displayIndex, activeRows) => {
                          const visitItem: any = item;
                          const visitIndex = procedureVisit.findIndex(
                            (v) => v.displayOrder === item.displayOrder,
                          );

                          const consumableCount =
                            item?.procedureVisitConsumableColl?.filter(
                              (c: any) => !c.isDeleted,
                            )?.length || 0;
                          const teamMemberCount =
                            item?.procedureVisitTeamMemberColl?.filter(
                              (t: any) => !t.isDeleted,
                            )?.length || 0;
                          const medicineCount =
                            item?.procedureVisitMedicineColl?.reduce(
                              (count: number, med: any) =>
                                count +
                                (med?.procedureVisitMedicineDetailColl
                                  ?.length || 0),
                              0,
                            );
                          const instructionCount =
                            item?.procedureVisitMedicineColl?.reduce(
                              (count: number, med: any) =>
                                count +
                                (med?.procedureVisitMedicineInstructionColl
                                  ?.length || 0),
                              0,
                            );

                          return (
                            <React.Fragment key={item.displayOrder}>
                              {displayIndex > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    my: 1.5,
                                  }}
                                >
                                  <Box className="procedure-wait-badge">
                                    <AccessTimeOutlinedIcon
                                      sx={{ fontSize: 14 }}
                                    />
                                    <Typography
                                      sx={{ fontSize: 12, fontWeight: 600 }}
                                    >
                                      Wait:
                                    </Typography>
                                    <Controller
                                      name={`procedureVisitColl.${visitIndex}.gapFromPrevVisitValue`}
                                      control={methods.control}
                                      render={({ field }) => (
                                        <CustomTextField
                                          {...field}
                                          size="small"
                                          type="number"
                                          placeholder="0"
                                          inputProps={inputPropsForNumberField}
                                          sx={{
                                            width: 68,
                                            "& .MuiOutlinedInput-root": {
                                              height: 28,
                                              background: "#fff",
                                            },
                                          }}
                                        />
                                      )}
                                    />
                                    <Controller
                                      name={`procedureVisitColl.${visitIndex}.gapFromPrevVisitCode`}
                                      control={methods.control}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          size="small"
                                          onChange={(e) => {
                                            const selectedOption =
                                              GapUnitOptions.find(
                                                (opt) =>
                                                  opt.value === e.target.value,
                                              );
                                            field.onChange(e.target.value);
                                            methods.setValue(
                                              `procedureVisitColl.${visitIndex}.gapFromPrevVisitName`,
                                              selectedOption?.label || "",
                                            );
                                          }}
                                          sx={{
                                            minWidth: 84,
                                            height: 28,
                                            bgcolor: "#fff",
                                            "& .MuiSelect-select": {
                                              py: 0.5,
                                              fontSize: 12,
                                              fontWeight: 600,
                                            },
                                          }}
                                        >
                                          {GapUnitOptions.map((opt) => (
                                            <MenuItem
                                              key={opt.value}
                                              value={opt.value}
                                            >
                                              {opt.label}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      )}
                                    />
                                    <Typography
                                      sx={{ fontSize: 12, fontWeight: 600 }}
                                    >
                                      before next procedure
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              <Box
                                sx={{
                                  border: "1px solid #e2e8f0",
                                  borderRadius: 2,
                                  backgroundColor: "#f8fafc",
                                  p: { xs: 1.75, sm: 2.25 },
                                  mb:
                                    displayIndex === activeRows.length - 1
                                      ? 0
                                      : 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: {
                                      xs: "flex-start",
                                      md: "center",
                                    },
                                    gap: 1.5,
                                    flexWrap: "wrap",
                                    mb: 1.5,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      // color: "#2563eb",
                                      fontWeight: 600,
                                      fontSize: 14,
                                    }}
                                  >
                                    Visit {displayIndex + 1}:{" "}
                                    {getVisitDisplayName(item.procedureName)}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: { xs: 1, md: 2 },
                                      ml: { xs: 0, md: "auto" },
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#334155",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      ₹{" "}
                                      {Number(
                                        visitItem.price || 0,
                                      ).toLocaleString("en-IN")}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#334155",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {`${visitItem.durationHoursValue || 0} hr ${visitItem.durationMinuteValue || 0} min`}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleDeleteProcedure(item)
                                      }
                                      sx={{ ml: { xs: 0, md: 0.5 } }}
                                    >
                                      <CloseRoundedIcon
                                        style={{ color: "#d32f2f" }}
                                      />
                                    </IconButton>
                                  </Box>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    pt: 1.5,
                                    borderTop: "1px dashed #e2e8f0",
                                  }}
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                      <Inventory2OutlinedIcon
                                        sx={{ fontSize: 14 }}
                                      />
                                    }
                                    className="procedure-chip-btn"
                                    sx={{
                                      borderStyle:
                                        consumableCount === 0
                                          ? "dashed"
                                          : "solid",
                                    }}
                                    onClick={() => {
                                      const active =
                                        item?.procedureVisitConsumableColl?.filter(
                                          (c: any) => !c.isDeleted,
                                        ) || [];
                                      setOpenConsumableCollDilog({
                                        data: active,
                                        open: true,
                                        totalCount: active.length || 0,
                                        visitIndex,
                                      });
                                    }}
                                  >
                                    {/* {consumableCount > 0
                                      ? `${consumableCount} Consumables`
                                      : "Add Consumable"} */}
                                    <span style={{ color: "#285cdb" }}>
                                      {consumableCount > 0
                                        ? `${consumableCount} Consumables`
                                        : "Add Consumable"}
                                    </span>
                                  </Button>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                      <Groups2OutlinedIcon
                                        sx={{ fontSize: 14 }}
                                      />
                                    }
                                    className="procedure-chip-btn"
                                    sx={{
                                      borderStyle:
                                        teamMemberCount === 0
                                          ? "dashed"
                                          : "solid",
                                    }}
                                    onClick={() => {
                                      const active =
                                        item?.procedureVisitTeamMemberColl?.filter(
                                          (t: any) => !t.isDeleted,
                                        ) || [];
                                      setOpenTeamMemberCollDilog({
                                        data: active,
                                        open: true,
                                        totalCount: active.length || 0,
                                        visitIndex,
                                      });
                                    }}
                                  >
                                    <span style={{ color: "#285cdb" }}>
                                      {teamMemberCount > 0
                                        ? `${teamMemberCount} Team Members`
                                        : "Add Team Member"}
                                    </span>
                                  </Button>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                      <MedicationOutlinedIcon
                                        sx={{ fontSize: 14 }}
                                      />
                                    }
                                    className="procedure-chip-btn"
                                    disabled={medicineCount === 0}
                                    sx={{
                                      borderStyle:
                                        medicineCount === 0
                                          ? "dashed"
                                          : "solid",
                                    }}
                                    onClick={() => {
                                      setOpenMedicineCollDilog({
                                        data: item.procedureVisitMedicineColl,
                                        open: true,
                                        totalCount: medicineCount,
                                      });
                                    }}
                                  >
                                    <span style={{ color: "#285cdb" }}>
                                      {medicineCount > 0
                                        ? `${medicineCount} Medicine`
                                        : "Add Medicine"}
                                    </span>
                                  </Button>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                      <AssignmentOutlinedIcon
                                        sx={{ fontSize: 14 }}
                                      />
                                    }
                                    className="procedure-chip-btn"
                                    disabled={instructionCount === 0}
                                    sx={{
                                      borderStyle:
                                        instructionCount === 0
                                          ? "dashed"
                                          : "solid",
                                    }}
                                    onClick={() => {
                                      setOpenInstructionCollDilog({
                                        data: item.procedureVisitMedicineColl,
                                        open: true,
                                        totalCount: instructionCount,
                                      });
                                    }}
                                  >
                                    <span style={{ color: "#285cdb" }}>
                                      {instructionCount > 0
                                        ? `${instructionCount} Instructions`
                                        : "Add Instruction"}
                                    </span>
                                  </Button>
                                </Box>
                              </Box>
                            </React.Fragment>
                          );
                        })}

                      {openVisit && (
                        <Box sx={{ mt: 2, mb: 1.5 }}>
                          <FormControl fullWidth>
                            <SimpleAsyncSelect
                              placeholder="Search Procedure"
                              id={"ProcedureSearch"}
                              FieldName={"Procedure"}
                              // menuPortalTarget={document.querySelector("body")}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 99999,
                                }),
                              }}
                              handleChange={async (e: any) => {
                                handleSingleProcedureVisitAdd(e);
                              }}
                              value={null}
                              loadOptions={loadProceduresList}
                              defaultOptions={visitProceduresColl || []}
                              isSearchable
                              isNotCreatable={true}
                            />
                          </FormControl>
                        </Box>
                      )}

                      {isFieldEnabled("SET_MULTIPLE_VISIT") && !openVisit && (
                        <Box
                          sx={{
                            mt: 1.25,
                            mb: 1.25,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<AddRoundedIcon />}
                            className="procedure-add-step-btn"
                            onClick={() => setOpenVisit(true)}
                          >
                            Add Next Visit Step
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box className="procedure-summary-footer">
                  <Box className="procedure-totals-display">
                    <Box className="procedure-total-item">
                      <Typography className="procedure-total-label">
                        Total Price
                      </Typography>
                      <Typography className="procedure-total-value">
                        ₹{" "}
                        {Number(methods.watch("price") || 0).toLocaleString(
                          "en-IN",
                        )}
                      </Typography>
                    </Box>
                    <Box className="procedure-total-item">
                      <Typography className="procedure-total-label">
                        Total Est. Duration
                      </Typography>
                      <Typography className="procedure-total-value">
                        {getNormalizedDurationText()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box className="procedure-actions">
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="outlined"
                      type="button"
                      className="secondary-button procedure-action-button"
                      name="cancel"
                      onClick={handleBack}
                      disabled={isLoading || methods.formState.isSubmitting}
                    >
                      Cancel
                    </LoadingButton>
                    {isForPopUp &&
                      isMemberProcedure &&
                      urlProcedureId === 0 && (
                        <LoadingButton
                          loading={isSaving}
                          color="primary"
                          loadingPosition="start"
                          startIcon={<></>}
                          variant="outlined"
                          type="button"
                          className="secondary-button procedure-action-button"
                          name="save-template"
                          onClick={handleSaveAndTemplate}
                          disabled={isLoading || methods.formState.isSubmitting}
                        >
                          Save
                        </LoadingButton>
                      )}
                    {!isForPopUp && (
                      <LoadingButton
                        loading={isSaving}
                        color="primary"
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="contained"
                        type="submit"
                        className="primary-button procedure-action-button"
                        name="save"
                        disabled={isLoading || methods.formState.isSubmitting}
                      >
                        {urlProcedureId > 0
                          ? "Update Procedure"
                          : "Save Procedure"}
                      </LoadingButton>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>

        <Dialog
          open={openConsumableCollDilog.open}
          onClose={() => handleCloseDilog(false)}
          PaperProps={{
            style: {
              width: "700px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #bdc1cd",
              p: 0,
            }}
          >
            <Box
              sx={{
                borderBottom: "1px solid #bdc1cd",
                display: "flex",
                justifyContent: "space-between",
                p: "16px",
              }}
            >
              <Box>Consumable</Box>
              <Box>{openConsumableCollDilog?.totalCount}</Box>
            </Box>
            <Grid
              container
              sx={{
                background: "#f1f5f8",
              }}
            >
              <Grid
                item
                xs={3}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Consumables Name
              </Grid>
              <Grid
                item
                xs={3}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Quantity
              </Grid>
              <Grid
                item
                xs={3}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Price
              </Grid>
              <Grid
                item
                xs={2}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Amount
              </Grid>
              <Grid
                item
                xs={1}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                  textAlign: "center",
                }}
              ></Grid>
            </Grid>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {openConsumableCollDilog.data?.map((item, index: number) => (
              <Grid
                container
                key={index}
                sx={{
                  borderTop: index === 0 ? "none" : "1px solid #e9e9ef",
                  alignItems: "center",
                }}
              >
                <Grid
                  item
                  xs={3}
                  className="font-size-14"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    className="text-link link-cursor"
                    onClick={() => {
                      const visitIndex =
                        openConsumableCollDilog.visitIndex ?? 0;
                      const collPath = `procedureVisitColl.${visitIndex}.procedureVisitConsumableColl`;
                      const rows = (methods.getValues as any)(collPath) || [];
                      const arrayIndex = rows.findIndex(
                        (r: any) => r.displayOrder === item.displayOrder,
                      );
                      setSelectedConsumable({
                        ...item,
                        visitIndex,
                        arrayIndex,
                        displayOrder: item.displayOrder,
                      });
                      handleClickOpenPopup(true, "", "UPDATE_ITEM", item);
                    }}
                  >
                    {item?.itemName?.trim()}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  className="font-size-14"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <CustomTextField
                    type="text"
                    value={item?.quantity ?? 0}
                    onChange={(e) =>
                      handleConsumableFieldChange(
                        openConsumableCollDilog.visitIndex ?? 0,
                        item.displayOrder,
                        "quantity",
                        e.target.value,
                      )
                    }
                    size="small"
                    placeholder="0"
                    inputProps={inputPropsForNumberField}
                    sx={{
                      width: "120px",
                      "& .MuiInputBase-root": {
                        height: "36px",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box
                            sx={{
                              borderLeft: "1px solid #c4c4c4",
                              paddingLeft: "8px",
                              paddingRight: "6px",
                              minWidth: "36px",
                              textAlign: "center",
                              fontSize: "12px",
                              color: "#6b7280",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item?.unitName || "-"}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  className="font-size-14"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <CustomTextField
                    value={item?.unitPrice ?? 0}
                    onChange={(e) =>
                      handleConsumableFieldChange(
                        openConsumableCollDilog.visitIndex ?? 0,
                        item.displayOrder,
                        "unitPrice",
                        e.target.value,
                      )
                    }
                    size="small"
                    placeholder="0"
                    inputProps={inputPropsForDecimalNumberField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              borderRight: "1px solid #c4c4c4",
                              width: "30px",
                              textAlign: "center",
                              paddingRight: "6px",
                            }}
                          >
                            Rs.
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: "120px" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  className="font-size-14"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  ₹ {item?.quantity * item?.unitPrice || "-"}
                </Grid>
                <Grid item xs={1} sx={{ px: 0, py: 1, textAlign: "center" }}>
                  <IconButton
                    size="small"
                    onClick={(e: any) => {
                      setSelectedConsumable({
                        ...item,
                        visitIndex: openConsumableCollDilog.visitIndex ?? 0,
                      });
                      setConsumableMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVertRoundedIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Box sx={{ p: 2 }}>
              <SimpleAsyncSelect
                id="consumable-dialog"
                FieldName="consumable"
                placeholder="Search or Create Consumable"
                defaultOptions={consumableColl}
                loadOptions={loadConsumableList}
                handleChange={(selected: any) => {
                  if (!selected) return;
                  const visitIndex = openConsumableCollDilog.visitIndex ?? 0;
                  handleAddConsumableToVisit(selected, visitIndex);
                  refreshConsumableDialog(visitIndex);
                }}
                value={null}
                menuPortalTarget={document.body}
                onCreateOption={(inputValue: string) => {
                  setSelectedConsumable({
                    visitIndex: openConsumableCollDilog.visitIndex ?? 0,
                    displayOrder: -1,
                  });
                  handleClickOpenPopup(true, inputValue, "CREATE_ITEM", null);
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "1px solid #bdc1cd",
            }}
          >
            <Button onClick={() => handleCloseDilog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openTeamMemberCollDilog.open}
          onClose={() => handleCloseDilog(false)}
          PaperProps={{
            style: {
              width: "700px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #bdc1cd",
              p: 0,
            }}
          >
            <Box
              sx={{
                borderBottom: "1px solid #bdc1cd",
                display: "flex",
                justifyContent: "space-between",
                p: "16px",
              }}
            >
              <Box>TeamMember</Box>
              <Box>{openTeamMemberCollDilog?.totalCount}</Box>
            </Box>
            <Grid
              container
              sx={{
                background: "#f1f5f8",
              }}
            >
              <Grid
                item
                xs={5}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                TeamMember Name
              </Grid>
              <Grid
                item
                xs={3}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Role
              </Grid>
              <Grid
                item
                xs={3}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Qualification
              </Grid>
              <Grid
                item
                xs={1}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                  textAlign: "center",
                }}
              ></Grid>
            </Grid>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {openTeamMemberCollDilog.data?.map((item, index: number) => (
              <Grid
                container
                key={index}
                sx={{
                  borderTop: index === 0 ? "none" : "1px solid #e9e9ef",
                  alignItems: "center",
                }}
              >
                <Grid
                  item
                  xs={5}
                  className="font-size-14"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    className="text-link link-cursor"
                    onClick={() => {
                      const visitIndex =
                        openTeamMemberCollDilog.visitIndex ?? 0;
                      const collPath = `procedureVisitColl.${visitIndex}.procedureVisitTeamMemberColl`;
                      const rows = (methods.getValues as any)(collPath) || [];
                      const arrayIndex = rows.findIndex(
                        (r: any) => r.displayOrder === item.displayOrder,
                      );
                      setSelectedTeamMember({
                        ...item,
                        visitIndex,
                        arrayIndex,
                        displayOrder: item.displayOrder,
                      });
                      handleClickOpenPopup(true, "", "UPDATE_TEAMMEMBER", item);
                    }}
                  >
                    {item?.teamMemberName?.trim()}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  className="font-size-14"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  {item?.teamMemberRole || "-"}
                </Grid>
                <Grid
                  item
                  xs={3}
                  className="font-size-14"
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  {item?.qualificationName || "-"}
                </Grid>
                <Grid item xs={1} sx={{ px: 0, py: 1, textAlign: "center" }}>
                  <IconButton
                    size="small"
                    onClick={(e: any) => {
                      setSelectedTeamMember({
                        ...item,
                        visitIndex: openTeamMemberCollDilog.visitIndex ?? 0,
                      });
                      setTeamMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVertRoundedIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Box sx={{ p: 2 }}>
              <SimpleAsyncSelect
                id="team-member-dialog"
                FieldName="teamMember"
                placeholder="Search or Create Team Member"
                defaultOptions={teamMembersColl}
                loadOptions={loadTeamMembersList}
                handleChange={(selected: any) => {
                  if (!selected) return;
                  const visitIndex = openTeamMemberCollDilog.visitIndex ?? 0;
                  handleAddTeamMember(selected, visitIndex);
                  refreshTeamMemberDialog(visitIndex);
                }}
                value={null}
                menuPortalTarget={document.body}
                onCreateOption={(inputValue: string) => {
                  setSelectedTeamMember({
                    visitIndex: openTeamMemberCollDilog.visitIndex ?? 0,
                    displayOrder: -1,
                  });
                  handleClickOpenPopup(
                    true,
                    inputValue,
                    "CREATE_TEAMMEMBER",
                    null,
                  );
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "1px solid #bdc1cd",
            }}
          >
            <Button onClick={() => handleCloseDilog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Menu
          anchorEl={consumableMenuAnchor}
          open={Boolean(consumableMenuAnchor)}
          onClose={() => setConsumableMenuAnchor(null)}
        >
          <MenuItem onClick={handleEditConsumable}>Edit</MenuItem>
          <MenuItem
            className="delete-container"
            onClick={() => {
              if (!selectedConsumable) return;
              const visitIndex = selectedConsumable.visitIndex ?? 0;
              handleDeleteConsumableRow(visitIndex, selectedConsumable);
              refreshConsumableDialog(visitIndex);
              setConsumableMenuAnchor(null);
            }}
          >
            Delete
          </MenuItem>
        </Menu>
        <Menu
          anchorEl={teamMenuAnchor}
          open={Boolean(teamMenuAnchor)}
          onClose={() => setTeamMenuAnchor(null)}
        >
          <MenuItem onClick={handleEditTeamMember}>Edit</MenuItem>
          <MenuItem
            className="delete-container"
            onClick={() => {
              if (!selectedTeamMember) return;
              const visitIndex = selectedTeamMember.visitIndex ?? 0;
              handleDeleteTeamMemberRow(visitIndex, selectedTeamMember);
              refreshTeamMemberDialog(visitIndex);
              setTeamMenuAnchor(null);
            }}
          >
            Delete
          </MenuItem>
        </Menu>
        <Dialog
          className="dialogPage"
          open={
            customPopUp.open &&
            (customPopUp.code === "CREATE_ITEM" ||
              customPopUp.code === "UPDATE_ITEM")
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") handleClosePopup();
          }}
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
                {customPopUp.code === "CREATE_ITEM" ? "Save Item" : "Edit Item"}
              </Typography>
            </Toolbar>
          </AppBar>
          <DialogContent sx={{ p: 2 }}>
            <ItemSave
              handleClosePopUp={(code: string, company: any) =>
                handleManagePopup(code, company)
              }
              isForPopUp={true}
              name={customPopUp.name}
              code={customPopUp.code}
              itemId={(customPopUp?.data as any)?.itemId || 0}
            />
          </DialogContent>
        </Dialog>
        <Dialog
          className="dialogPage"
          open={
            customPopUp.open &&
            (customPopUp.code === "CREATE_TEAMMEMBER" ||
              customPopUp.code === "UPDATE_TEAMMEMBER")
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") handleClosePopup();
          }}
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
                {customPopUp.code === "CREATE_TEAMMEMBER"
                  ? "Save Team Member"
                  : "Edit Team Member"}
              </Typography>
            </Toolbar>
          </AppBar>
          <DialogContent sx={{ p: 2 }}>
            <TeamMemberSave
              handleClosePopUp={(code: string, company: any) =>
                handleManagePopup(code, company)
              }
              isForPopUp={true}
              name={customPopUp.name}
              teamMemberId={(customPopUp?.data as any)?.teamMemberId || 0}
            />
          </DialogContent>
        </Dialog>
        <Dialog
          open={openMedicineCollDilog.open}
          onClose={() => handleCloseDilog(false)}
          PaperProps={{
            style: {
              width: "700px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #bdc1cd",
              p: 0,
            }}
          >
            <Box
              sx={{
                borderBottom: "1px solid #bdc1cd",
                display: "flex",
                justifyContent: "space-between",
                p: "16px",
              }}
            >
              <Box>Medicine</Box>
              <Box>{openMedicineCollDilog?.totalCount}</Box>
            </Box>
            <Grid
              container
              sx={{
                background: "#f1f5f8",
              }}
            >
              <Grid
                item
                xs={9}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Medicine Name
              </Grid>
              <Grid
                item
                xs={3}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Print Unit
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {openMedicineCollDilog.data?.map((item, index) => (
              <React.Fragment key={index}>
                {item?.procedureVisitMedicineDetailColl?.map(
                  (med: any, medIndex: number) => (
                    <Grid
                      container
                      key={medIndex}
                      sx={{
                        borderTop:
                          index === 0 && medIndex === 0
                            ? "none"
                            : "1px solid #e9e9ef",
                      }}
                    >
                      <Grid
                        item
                        xs={9}
                        className="font-size-14"
                        sx={{ px: 2, py: 1 }}
                      >
                        {med?.medicineName?.trim()}
                      </Grid>

                      <Grid
                        item
                        xs={3}
                        className="font-size-14"
                        sx={{ px: 2, py: 1 }}
                      >
                        {med?.printUnit || "-"}
                      </Grid>
                    </Grid>
                  ),
                )}
              </React.Fragment>
            ))}
          </DialogContent>

          <DialogActions
            sx={{
              borderTop: "1px solid #bdc1cd",
            }}
          >
            <Button onClick={() => handleCloseDilog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openInstructionCollDilog.open}
          onClose={() => handleCloseDilog(false)}
          PaperProps={{
            style: {
              width: "700px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #bdc1cd",
              p: 0,
            }}
          >
            <Box
              sx={{
                borderBottom: "1px solid #bdc1cd",
                display: "flex",
                justifyContent: "space-between",
                p: "16px",
              }}
            >
              <Box>Instruction</Box>
              <Box>{openInstructionCollDilog?.totalCount}</Box>
            </Box>
            <Grid
              container
              sx={{
                background: "#f1f5f8",
              }}
            >
              <Grid
                item
                xs={12}
                className="font-size-14"
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                Instruction Detail
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {openInstructionCollDilog.data?.map((item, index) => (
              <React.Fragment key={index}>
                {item?.procedureVisitMedicineInstructionColl?.map(
                  (med: any, medIndex: number) => (
                    <Grid
                      container
                      key={medIndex}
                      sx={{
                        borderTop:
                          index === 0 && medIndex === 0
                            ? "none"
                            : "1px solid #e9e9ef",
                      }}
                    >
                      <Grid
                        item
                        xs={9}
                        className="font-size-14"
                        sx={{ px: 2, py: 1 }}
                      >
                        {parse(med?.instructionData || "")}
                      </Grid>
                    </Grid>
                  ),
                )}
              </React.Fragment>
            ))}
          </DialogContent>

          <DialogActions
            sx={{
              borderTop: "1px solid #bdc1cd",
            }}
          >
            <Button onClick={() => handleCloseDilog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};
export default MultipleProcedureVisit;
